import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { ListRow } from "components/designV2/listRow/listRow";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { type CompanyGigApplications } from "model/Gig";
import { ApplicationStatus } from "model/Application";
import { useTranslation } from "react-i18next";
import {
  ActionButtons,
  IconActionButton,
  RequestedRateBlock,
  RequestedRateLabel,
  RequestedRateValue,
} from "./teamPublicDetailsModal.styled";

interface TeamPublicDetailsModalApplicationRowBaseProps {
  application: CompanyGigApplications;
  avatarUrl?: string;
  isBusy: boolean;
  onOpenMotivationalLetterModal: (motivationalLetter: string) => void;
}

interface TeamPublicDetailsModalApplicationRowManageableProps extends TeamPublicDetailsModalApplicationRowBaseProps {
  canManageApplication: true;
  onOpenApplicationActionConfirm: (
    applicationId: number,
    status: ApplicationStatus.OFFERED | ApplicationStatus.CLOSED,
    applicantName: string,
  ) => void;
}

interface TeamPublicDetailsModalApplicationRowReadOnlyProps extends TeamPublicDetailsModalApplicationRowBaseProps {
  canManageApplication: false;
  onOpenApplicationActionConfirm?: never;
}

type TeamPublicDetailsModalApplicationRowProps =
  | TeamPublicDetailsModalApplicationRowManageableProps
  | TeamPublicDetailsModalApplicationRowReadOnlyProps;

export const TeamPublicDetailsModalApplicationRow: React.FC<
  TeamPublicDetailsModalApplicationRowProps
> = (props) => {
  const { t } = useTranslation();
  const firstName = props.application.worker.firstName?.trim() ?? "";
  const lastName = props.application.worker.lastName?.trim() ?? "";
  const fullName = `${firstName} ${lastName}`.trim() || "-";
  const motivationalLetter = props.application.motivationalLetter?.trim() ?? "";
  const requestedRateLabel =
    props.application.requestedRate == null
      ? "-"
      : `${props.application.requestedRate} kr/h`;

  return (
    <ListRow variant="light">
      <AvatarChip title={fullName} avatarUrl={props.avatarUrl} />
      <RequestedRateBlock>
        <RequestedRateValue>{requestedRateLabel}</RequestedRateValue>
        <RequestedRateLabel>
          {t("Schedule.TeamPublicRequestedRate")}
        </RequestedRateLabel>
      </RequestedRateBlock>
      <ActionButtons>
        <TooltipHover
          text={t("Schedule.TeamPublicMotivationalLetter")}
          arrow="down"
        >
          <IconActionButton
            type="button"
            aria-label={t("Schedule.TeamPublicMotivationalLetter")}
            disabled={!motivationalLetter}
            onClick={() => props.onOpenMotivationalLetterModal(motivationalLetter)}
          >
            <CustomIcon name="v2/envelope" size="24px" color={ColorV2.Blue3} />
          </IconActionButton>
        </TooltipHover>
        {props.canManageApplication && (
          <>
            <TooltipHover text={t("Schedule.TeamPublicAccept")} arrow="down">
              <IconActionButton
                type="button"
                disabled={props.isBusy}
                aria-label={t("Schedule.TeamPublicAccept")}
                onClick={() =>
                  props.onOpenApplicationActionConfirm(
                    props.application.id,
                    ApplicationStatus.OFFERED,
                    fullName,
                  )
                }
              >
                <CustomIcon
                  name="v2/checkmark"
                  size="26px"
                  color={ColorV2.PositiveGreen}
                />
              </IconActionButton>
            </TooltipHover>
            <TooltipHover text={t("Schedule.TeamPublicDecline")} arrow="down">
              <IconActionButton
                type="button"
                disabled={props.isBusy}
                aria-label={t("Schedule.TeamPublicDecline")}
                onClick={() =>
                  props.onOpenApplicationActionConfirm(
                    props.application.id,
                    ApplicationStatus.CLOSED,
                    fullName,
                  )
                }
              >
                <CustomIcon
                  name="v2/cross"
                  size="26px"
                  color={ColorV2.NegativeRed}
                />
              </IconActionButton>
            </TooltipHover>
          </>
        )}
      </ActionButtons>
    </ListRow>
  );
};
